import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axiosInstance from "../../utils/axios/axiosInstance";
import { FaChevronLeft, FaChevronRight, FaHeart, FaRegHeart, FaMapMarkerAlt, FaPhone, FaEnvelope, FaSearchPlus, FaTimes, FaCheckCircle, FaClock, FaUsers, FaBell, FaExclamationCircle, FaMoneyBillWave, FaCreditCard, FaPaypal, FaMobileAlt, FaCalendarAlt  } from 'react-icons/fa';
import { toast } from 'react-toastify';
import loadingGif from '../../assets/images/loading.gif';

const MarketplaceDetailsPage = () => {
  const { id, slug } = useParams();
  
  // DEBUG: Add this temporarily to see what's happening
  useEffect(() => {
    console.log('🔍 Debug Info:', {
      id,
      slug,
      currentLocation: window.location.href,
      pathname: window.location.pathname,
      hash: window.location.hash
    });
  }, [id, slug]);

const createSlug = useCallback((title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}, []);

  const navigate = useNavigate();

  // Helper function to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Helper function to get minimum allowed time based on selected date
const getMinimumTime = useCallback((selectedDate) => {
  if (!selectedDate) return '';
  
  const today = getCurrentDate();
  const now = new Date();
  
  // If selected date is today, minimum time should be current time + 30 minutes buffer
  if (selectedDate === today) {
    const futureTime = new Date(now.getTime() + 30 * 60 * 1000); // Add 30 minutes
    const hours = futureTime.getHours().toString().padStart(2, '0');
    const minutes = futureTime.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  
  // If selected date is in the future, no minimum time restriction
  return '';
}, []);

  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const pollIntervalRef = useRef(null);

  // Helper function to get item condition with fallback options
  const getItemCondition = useCallback((item) => {
    if (!item) return 'Not specified';
    
    // First check for readable condition names
    const conditionName = item.condition_name || 
                         item.conditionName ||
                         item.condition_text ||
                         item.conditionText;
    
    if (conditionName && typeof conditionName === 'string' && conditionName.trim()) {
      const readableName = conditionName.trim();
      console.log('✅ Found readable condition name:', readableName);
      return readableName.charAt(0).toUpperCase() + readableName.slice(1).toLowerCase();
    }
    
    // Then check for condition IDs that need mapping
    const conditionValue = item.condition || 
                          item.item_condition || 
                          item.product_condition || 
                          item.ad_condition ||
                          item.state ||
                          item.item_state ||
                          item.condition_id ||
                          item.conditionId;
    
    // Enhanced debug logging for condition field
    console.log('🔍 Condition Debug Info:', {
      conditionValue,
      type: typeof conditionValue,
      condition_name: item.condition_name,
      conditionName: item.conditionName,
      condition: item.condition,
      condition_id: item.condition_id,
      conditionId: item.conditionId,
      state: item.state,
      allItemKeys: Object.keys(item)
    });
    
    // Enhanced condition mapping with more possibilities
    if (typeof conditionValue === 'number' || (typeof conditionValue === 'string' && !isNaN(parseInt(conditionValue)))) {
      const numericValue = parseInt(conditionValue);
      
      // Multiple mapping possibilities based on common condition ID patterns
      const conditionMaps = [
        // Standard 1-5 mapping
        {
          1: 'New',
          2: 'Like New', 
          3: 'Good',
          4: 'Fair',
          5: 'Poor',
          0: 'Not specified'
        },
        // Alternative 0-4 mapping
        {
          0: 'New',
          1: 'Like New',
          2: 'Good', 
          3: 'Fair',
          4: 'Poor'
        },
        // Reverse 5-1 mapping (some systems use reverse)
        {
          5: 'New',
          4: 'Like New',
          3: 'Good',
          2: 'Fair', 
          1: 'Poor'
        },
        // Extended mapping for systems with more conditions
        {
          1: 'Brand New',
          2: 'Excellent',
          3: 'Very Good',
          4: 'Good',
          5: 'Fair',
          6: 'Poor',
          7: 'For Parts',
          8: 'Damaged',
          0: 'Not specified'
        },
        // Another common pattern
        {
          10: 'New',
          20: 'Like New',
          30: 'Good',
          40: 'Fair',
          50: 'Poor'
        }
      ];
      
      // Try each mapping until we find a match
      for (const map of conditionMaps) {
        if (map[numericValue]) {
          console.log(`✅ Condition mapped: ${numericValue} -> ${map[numericValue]}`);
          return map[numericValue];
        }
      }
      
      // If no mapping found, return a generic description
      console.log(`⚠️ No condition mapping found for value: ${numericValue}`);
      return `Condition ${numericValue}`;
    }
    
    // If it's already a readable string, return it with proper capitalization
    if (typeof conditionValue === 'string' && conditionValue.trim()) {
      const trimmedValue = conditionValue.trim();
      
      // Check if it's already a readable condition (not just a number as string)
      const readableConditions = ['new', 'like new', 'excellent', 'very good', 'good', 'fair', 'poor', 'used', 'refurbished'];
      const isReadableCondition = readableConditions.some(condition => 
        trimmedValue.toLowerCase().includes(condition)
      );
      
      if (isReadableCondition || trimmedValue.length > 2) {
        return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1).toLowerCase();
      } else {
        // If it's just a number as string, try to parse it
        const numericValue = parseInt(trimmedValue);
        if (!isNaN(numericValue)) {
          return `Condition ${numericValue}`;
        }
      }
    }
    
    console.log('❌ No condition value found, returning default');
    return 'Not specified';
  }, []);

  // FIXED: Move normalizeStatus function to the top and make it more robust
  const normalizeStatus = useCallback((status) => {
    if (!status) return 'pending';
    const normalized = status.toString().toLowerCase().trim();
    
    // Handle variations in status values
    if (normalized === 'accept' || normalized === 'accepted' || normalized === 'approved' || normalized === '1') return 'accepted';
    if (normalized === 'reject' || normalized === 'rejected' || normalized === '2') return 'rejected';
    if (normalized === 'pending' || normalized === '0') return 'pending';
    
    console.log('Unknown status format:', status, 'normalized to pending');
    return 'pending';
  }, []);

  // Helper function to safely access localStorage
  const safeLocalStorage = {
    getItem: (key) => {
      try {
        return typeof window !== 'undefined' ? localStorage.getItem(key) : null;
      } catch (error) {
        console.warn('localStorage not available:', error);
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(key, value);
        }
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    },
    removeItem: (key) => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
    }
  };

  // State declarations
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isContactVisible, setIsContactVisible] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  
  // Enhanced state for submitted offer with more details
  const [submittedOffer, setSubmittedOffer] = useState(null);
  const [offerStatusHistory, setOfferStatusHistory] = useState([]);

  // Updated state for buyer requests from API response
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Add polling state for status updates
  const [isPollingStatus, setIsPollingStatus] = useState(false);

  // Add loading state for profile fetching
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Buy Now Modal State
  const [isBuyNowModalOpen, setIsBuyNowModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showBuyNowForm, setShowBuyNowForm] = useState(false);
  const [showOnlinePaymentOptions, setShowOnlinePaymentOptions] = useState(false);
  const [buyNowStep, setBuyNowStep] = useState('payment-method');
  const [buyNowForm, setBuyNowForm] = useState({
    name: '',
    pickup_location: '',
    pickup_date: getCurrentDate(),
    pickup_time: getCurrentTime()
  });
  const [isSubmittingBuyNow, setIsSubmittingBuyNow] = useState(false);

  // Offer Modal State
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({
    name: '',
    pickup_location: '',
    amount: '',
    pickup_date: getCurrentDate(),
    pickup_time: getCurrentTime()
  });
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);

  // Update forms when userProfile changes
  useEffect(() => {
    if (userProfile) {
      const pickupLocation = item?.shipping_option === 1 
        ? 'Seller will arrange delivery' 
        : userProfile.address || '';
      
      // Update Buy Now form
      setBuyNowForm(prev => ({
        ...prev,
        name: userProfile.name || prev.name,
        pickup_location: pickupLocation
      }));

      // Update Offer form
      setOfferForm(prev => ({
        ...prev,
        name: userProfile.name || prev.name,
        pickup_location: pickupLocation
      }));
    }
  }, [userProfile, item?.shipping_option]);

  useEffect(() => {
    if (userProfile) {
      console.log('🔄 Updating forms with profile data:', userProfile);
      console.log('📦 Current item shipping option:', item?.shipping_option);
      
      const pickupLocation = item?.shipping_option === 1 
        ? 'Seller will arrange delivery' 
        : userProfile.address || '';
      
      console.log('📍 Calculated pickup location:', pickupLocation);
      
      // Update Buy Now form
      setBuyNowForm(prev => {
        const newForm = {
          ...prev,
          name: userProfile.name || prev.name,
          pickup_location: pickupLocation
        };
        console.log('💰 Updated Buy Now form:', newForm);
        return newForm;
      });

      // Update Offer form
      setOfferForm(prev => {
        const newForm = {
          ...prev,
          name: userProfile.name || prev.name,
          pickup_location: pickupLocation
        };
        console.log('🎯 Updated Offer form:', newForm);
        return newForm;
      });
    }
  }, [userProfile, item?.shipping_option]);

  // Helper function to get submitted offer storage key
  const getSubmittedOfferKey = useCallback((itemId) => {
    return `marketplace_submitted_offer_${itemId}`;
  }, []);

  // Helper function to get offer status history storage key
  const getOfferStatusHistoryKey = useCallback((itemId) => {
    return `marketplace_offer_status_history_${itemId}`;
  }, []);

  // Helper function to load submitted offer from localStorage
  const loadSubmittedOffer = useCallback((itemId) => {
    if (!itemId) return null;
    
    try {
      const storedOffer = safeLocalStorage.getItem(getSubmittedOfferKey(itemId));
      if (storedOffer) {
        return JSON.parse(storedOffer);
      }
    } catch (error) {
      console.warn('Error loading submitted offer from localStorage:', error);
      safeLocalStorage.removeItem(getSubmittedOfferKey(itemId));
    }
    return null;
  }, [getSubmittedOfferKey]);

  // Helper function to save submitted offer to localStorage
  const saveSubmittedOffer = useCallback((itemId, offerData) => {
    if (!itemId || !offerData) return;
    
    try {
      safeLocalStorage.setItem(getSubmittedOfferKey(itemId), JSON.stringify(offerData));
    } catch (error) {
      console.warn('Error saving submitted offer to localStorage:', error);
    }
  }, [getSubmittedOfferKey]);

  // Helper function to remove submitted offer from localStorage
  const removeSubmittedOffer = useCallback((itemId) => {
    if (!itemId) return;
    
    try {
      safeLocalStorage.removeItem(getSubmittedOfferKey(itemId));
      safeLocalStorage.removeItem(getOfferStatusHistoryKey(itemId));
    } catch (error) {
      console.warn('Error removing submitted offer from localStorage:', error);
    }
  }, [getSubmittedOfferKey, getOfferStatusHistoryKey]);

  // Helper function to add status to history
  const addStatusToHistory = useCallback((itemId, status, timestamp = null) => {
    if (!itemId || !status) return;
    
    try {
      const historyKey = getOfferStatusHistoryKey(itemId);
      const existingHistory = JSON.parse(safeLocalStorage.getItem(historyKey) || '[]');
      
      const newEntry = {
        status,
        timestamp: timestamp || new Date().toISOString(),
        id: Date.now()
      };
      
      // Avoid duplicates
      const lastEntry = existingHistory[existingHistory.length - 1];
      if (!lastEntry || lastEntry.status !== status) {
        existingHistory.push(newEntry);
        safeLocalStorage.setItem(historyKey, JSON.stringify(existingHistory));
        if (isMountedRef.current) {
          setOfferStatusHistory(existingHistory);
        }
      }
    } catch (error) {
      console.warn('Error adding status to history:', error);
    }
  }, [getOfferStatusHistoryKey]);

  // Enhanced payment method validation
  const validatePaymentMethods = useCallback(() => {
    if (!item) return { paypal: { available: false }, venmo: { available: false } };
    
    const validation = {
      paypal: {
        available: false,
        field: null,
        value: null,
        issues: []
      },
      venmo: {
        available: false,
        field: null,
        value: null,
        issues: []
      }
    };

    // Check PayPal
    const paypalFields = ['paypal_id', 'paypal_link', 'paypal_url', 'payment_paypal', 'paypal_payment_id'];
    for (const field of paypalFields) {
      if (item[field] && item[field].trim()) {
        validation.paypal.available = true;
        validation.paypal.field = field;
        validation.paypal.value = item[field];
        break;
      }
    }

    // Fallback to payment_id if payment_option is 2 (PayPal)
    if (!validation.paypal.available && item.payment_option === 2 && item.payment_id) {
      validation.paypal.available = true;
      validation.paypal.field = 'payment_id';
      validation.paypal.value = item.payment_id;
    }

    // Check Venmo
    const venmoFields = ['venmo_id', 'venmo_link', 'venmo_url', 'payment_venmo', 'venmo_payment_id', 'venmo_username'];
    for (const field of venmoFields) {
      if (item[field] && item[field].trim()) {
        validation.venmo.available = true;
        validation.venmo.field = field;
        validation.venmo.value = item[field];
        break;
      }
    }

    // Fallback to payment_id if payment_option is 1 (Venmo)
    if (!validation.venmo.available && item.payment_option === 1 && item.payment_id) {
      validation.venmo.available = true;
      validation.venmo.field = 'payment_id';
      validation.venmo.value = item.payment_id;
    }

    // Validate PayPal format
    if (validation.paypal.available) {
      const paypalValue = validation.paypal.value;
      if (!paypalValue.includes('paypal') && !paypalValue.includes('@')) {
        validation.paypal.issues.push('PayPal ID should contain "paypal" or be an email');
      }
    }

    // Validate Venmo format
    if (validation.venmo.available) {
      const venmoValue = validation.venmo.value;
      if (!venmoValue.includes('venmo') && !venmoValue.startsWith('@')) {
        validation.venmo.issues.push('Venmo ID should contain "venmo" or start with @');
      }
    }

    console.log('💳 Payment Method Validation:', validation);
    return validation;
  }, [item]);

  // Helper function to check if payment methods are available
  const getAvailablePaymentMethods = useCallback(() => {
    if (!item) return [];
    
    const methods = [];
    const validation = validatePaymentMethods();
    
    if (validation.paypal.available) methods.push('paypal');
    if (validation.venmo.available) methods.push('venmo');
    
    return methods;
  }, [item, validatePaymentMethods]);

  const isCashOnlyPayment = useCallback(() => {
    if (!item) return false;

    if (item.cash_only === true || item.payment_mode === 1) {
      return true;
    }

    const availableMethods = getAvailablePaymentMethods();
    return availableMethods.length === 0;
  }, [item, getAvailablePaymentMethods]);
  
  // FIXED: Improved checkOfferStatus function with better error handling and logging
  const checkOfferStatus = useCallback(async (itemId) => {
    if (!itemId || !submittedOffer?.id || !isMountedRef.current) {
      return null;
    }
    
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/detail/${itemId}`
      );

      if (response.data.status === 200 && response.data.data.buyer_requests && isMountedRef.current) {
        // Find the current user's offer by comparing IDs
        const currentOffer = response.data.data.buyer_requests.find(
          request => parseInt(request.id) === parseInt(submittedOffer.id)
        );

        if (currentOffer) {
          const newStatus = normalizeStatus(currentOffer.status);
          const currentStatus = normalizeStatus(submittedOffer.status);
          
          if (newStatus !== currentStatus && isMountedRef.current) {
            // Status has changed!
            const updatedOffer = {
              ...submittedOffer,
              status: newStatus,
              updatedAt: new Date().toISOString()
            };

            setSubmittedOffer(updatedOffer);
            saveSubmittedOffer(itemId, updatedOffer);
            addStatusToHistory(itemId, newStatus);

            // Update the item data as well to reflect the new status
            setItem(response.data.data);

            // Show notification for status change
            if (newStatus === 'accepted') {
              toast.success('🎉 Great news! Your offer has been accepted by the seller!', {
                position: "top-center",
                autoClose: 5000,
              });
            } else if (newStatus === 'rejected') {
              toast.error('Your offer was not accepted by the seller. You can make a new offer.', {
                position: "top-center",
                autoClose: 5000,
              });
            }

            return newStatus;
          }
        }
      }
    } catch (error) {
      console.error('❌ Error checking offer status:', error);
    }
    return null;
  }, [submittedOffer, saveSubmittedOffer, addStatusToHistory, normalizeStatus]);

  // Start polling for status updates
  const startStatusPolling = useCallback(() => {
    if (!submittedOffer || normalizeStatus(submittedOffer.status) !== 'pending' || isPollingStatus || !isMountedRef.current) {
      return;
    }
    
    setIsPollingStatus(true);
    
    // Initial check
    checkOfferStatus(parseInt(id));
    
    // Poll every 10 seconds for faster updates
    pollIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) {
        clearInterval(pollIntervalRef.current);
        return;
      }
      
      const newStatus = await checkOfferStatus(parseInt(id));
      
      // Stop polling if status is no longer pending
      if (newStatus && newStatus !== 'pending' && isMountedRef.current) {
        setIsPollingStatus(false);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, 10000); // Poll every 10 seconds
  }, [submittedOffer, isPollingStatus, checkOfferStatus, id, normalizeStatus]);

  // Stop polling
  const stopStatusPolling = useCallback(() => {
    setIsPollingStatus(false);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Manual refresh status function
  const manualRefreshStatus = useCallback(async () => {
    if (!submittedOffer || !id) {
      toast.info('No offer to refresh', {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    
    toast.info('Checking for status updates...', {
      position: "top-center",
      autoClose: 2000,
    });
    
    const newStatus = await checkOfferStatus(parseInt(id));
    
    if (newStatus) {
      toast.success(`Status updated to: ${newStatus}`, {
        position: "top-center",
        autoClose: 3000,
      });
    } else {
      toast.info('No status changes found', {
        position: "top-center",
        autoClose: 2000,
      });
    }
  }, [submittedOffer, id, checkOfferStatus]);

  // Check if current user is the seller
  const isCurrentUserSeller = useCallback(() => {
    return currentUser && item && currentUser.id === item.seller?.id;
  }, [currentUser, item]);

  // Check if user has already submitted an offer
  const hasSubmittedOffer = useCallback(() => {
    return submittedOffer !== null;
  }, [submittedOffer]);

  // Safe image navigation functions
  const nextImage = useCallback(() => {
    if (!item?.images || item.images.length === 0) return;
    setCurrentImageIndex(prevIndex =>
      prevIndex === item.images.length - 1 ? 0 : prevIndex + 1
    );
  }, [item?.images]);

  const prevImage = useCallback(() => {
    if (!item?.images || item.images.length === 0) return;
    setCurrentImageIndex(prevIndex =>
      prevIndex === 0 ? item.images.length - 1 : prevIndex - 1
    );
  }, [item?.images]);

  const goToImage = useCallback((index) => {
    if (!item?.images || index < 0 || index >= item.images.length) return;
    setCurrentImageIndex(index);
  }, [item?.images]);

  // Check wishlist status
  const checkWishlistStatus = useCallback(async (itemId) => {
    if (!itemId) return;
    
    try {
      // Only check API response for wishlist status
      const isInAPIWishlist = item?.is_wishlisted === true;
      
      if (isMountedRef.current) {
        setIsWishlisted(isInAPIWishlist);
      }
    } catch (error) {
      console.error('Error checking wishlist status:', error);
    }
  }, [item?.is_wishlisted]);

  // Enhanced PayPal payment handler
  const handlePayPalPayment = useCallback(() => {
    const validation = validatePaymentMethods();
    
    if (!validation.paypal.available) {
      toast.error('PayPal payment not available for this item', {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (validation.paypal.issues.length > 0) {
      toast.warning(`PayPal setup issues: ${validation.paypal.issues.join(', ')}`, {
        position: "top-center",
        autoClose: 4000,
      });
    }

    const paymentAmount = submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted' ? submittedOffer.amount : item.price;
    let paypalLink = validation.paypal.value;

    // Enhanced PayPal link processing
    try {
      if (paypalLink.includes('paypal.me/')) {
        // Handle paypal.me links
        const url = new URL(paypalLink.startsWith('http') ? paypalLink : `https://${paypalLink}`);
        const pathParts = url.pathname.split('/').filter(part => part);
        
        if (pathParts.length >= 1) {
          // pathParts[0] is the username, pathParts[1] might be existing amount
          if (pathParts.length >= 2 && !isNaN(parseFloat(pathParts[1]))) {
            pathParts[1] = paymentAmount.toString();
          } else {
            pathParts.push(paymentAmount.toString());
          }
          url.pathname = '/' + pathParts.join('/');
        }
        
        paypalLink = url.toString();
      } else if (paypalLink.includes('@') || paypalLink.includes('paypal.com')) {
        // Handle email or regular PayPal links
        if (paypalLink.includes('@')) {
          // Email format - convert to paypal.me
          const email = paypalLink.replace('mailto:', '');
          paypalLink = `https://paypal.me/${email}/${paymentAmount}`;
        } else {
          // Existing PayPal URL - add amount parameter
          const url = new URL(paypalLink.startsWith('http') ? paypalLink : `https://${paypalLink}`);
          url.searchParams.set('amount', paymentAmount.toString());
          url.searchParams.set('currency_code', 'INR');
          paypalLink = url.toString();
        }
      } else {
        // Plain username or ID
        paypalLink = `https://paypal.me/${paypalLink}/${paymentAmount}`;
      }
    } catch (error) {
      console.error('Error processing PayPal link:', error);
      toast.error('Invalid PayPal payment information', {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    console.log('🔗 Opening PayPal link:', paypalLink);
    
    toast.info('Opening PayPal payment...', {
      position: "top-center",
      autoClose: 2000,
    });

    window.open(paypalLink, '_blank');

    toast.success('PayPal opened! Complete payment and click "Payment Completed" below.', {
      position: "top-center",
      autoClose: 4000,
    });
  }, [validatePaymentMethods, submittedOffer, item, normalizeStatus]);

  // Enhanced Venmo payment handler
  const handleVenmoPayment = useCallback(() => {
    const validation = validatePaymentMethods();
    
    if (!validation.venmo.available) {
      toast.error('Venmo payment not available for this item', {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (validation.venmo.issues.length > 0) {
      toast.warning(`Venmo setup issues: ${validation.venmo.issues.join(', ')}`, {
        position: "top-center",
        autoClose: 4000,
      });
    }

    const paymentAmount = submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted' ? submittedOffer.amount : item.price;
    let venmoLink = validation.venmo.value;

    // Enhanced Venmo link processing
    try {
      if (venmoLink.includes('venmo.com/')) {
        // Handle existing Venmo URLs
        const url = new URL(venmoLink.startsWith('http') ? venmoLink : `https://${venmoLink}`);
        url.searchParams.set('txn', 'pay');
        url.searchParams.set('amount', paymentAmount.toString());
        url.searchParams.set('note', `Purchase of ${item.title}`);
        venmoLink = url.toString();
      } else {
        // Handle username/ID
        const username = venmoLink.replace('@', '').replace('venmo.com/', '');
        const note = encodeURIComponent(`Purchase of ${item.title} for $ ${paymentAmount}`);
        venmoLink = `https://venmo.com/${username}?txn=pay&amount=${paymentAmount}&note=${note}`;
      }
    } catch (error) {
      console.error('Error processing Venmo link:', error);
      toast.error('Invalid Venmo payment information', {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    console.log('🔗 Opening Venmo link:', venmoLink);
    
    toast.info('Opening Venmo payment...', {
      position: "top-center",
      autoClose: 2000,
    });

    window.open(venmoLink, '_blank');

    toast.success('Venmo opened! Complete payment and click "Payment Completed" below.', {
      position: "top-center",
      autoClose: 4000,
    });
  }, [validatePaymentMethods, submittedOffer, item, normalizeStatus]);

  // Fetch user profile data
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}/v1/profile`);
      
      if (response.data?.status === 200 && response.data?.data) {
        const profileData = response.data.data;
        
        const profile = {
          name: profileData.name || '',
          address: profileData.address || '',
          email: profileData.email || '',
          phone_number: profileData.phone_number || ''
        };
        
        setUserProfile(profile);
        
        // Update forms immediately with new profile data
        setBuyNowForm(prev => ({
          ...prev,
          name: profile.name,
          pickup_location: item?.shipping_option === 1 ? 'Seller will arrange delivery' : profile.address
        }));
        
        setOfferForm(prev => ({
          ...prev,
          name: profile.name,
          pickup_location: item?.shipping_option === 1 ? 'Seller will arrange delivery' : profile.address
        }));
      }
    } catch (error) {
      console.warn('Could not fetch user profile:', error);
    }
  }, [item?.shipping_option]);

  // Fetch current user info
  useEffect(() => {
    let mounted = true;
    
    const fetchCurrentUser = async () => {
      try {
        setCurrentUserLoading(true);
        
        const cachedUser = safeLocalStorage.getItem('currentUser');
        if (cachedUser && mounted) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            setCurrentUser(parsedUser);
          } catch (parseError) {
            console.warn('Error parsing cached user:', parseError);
            safeLocalStorage.removeItem('currentUser');
          }
        }
        
        const response = await axiosInstance.get('/auth/me');
        if (mounted) {
          setCurrentUser(response.data.user);
          safeLocalStorage.setItem('currentUser', JSON.stringify(response.data.user));
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        if (mounted) {
          setCurrentUser(null);
          safeLocalStorage.removeItem('currentUser');
        }
      } finally {
        if (mounted) {
          setCurrentUserLoading(false);
        }
      }
    };

    fetchCurrentUser();
    
    return () => {
      mounted = false;
    };
  }, []);

  // FIXED: Updated fetch item details to properly handle hash routing
  useEffect(() => {
    let mounted = true;
    
    const fetchItemDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/detail/${id}`
        );

        if (!mounted) return;

        if (response.data.status !== 200) {
          throw new Error(response.data.message || 'Failed to fetch item details');
        }

        const itemData = response.data.data;
        
        // Debug logging to see what fields are available
        console.log('📊 Item data fields:', Object.keys(itemData));
        console.log('📊 Condition field value:', itemData.condition);
        console.log('📊 Alternative condition fields:', {
          item_condition: itemData.item_condition,
          product_condition: itemData.product_condition,
          ad_condition: itemData.ad_condition,
          state: itemData.state,
          item_state: itemData.item_state
        });

        // FIXED: Proper hash routing URL update
        if (itemData.title) {
          const correctSlug = createSlug(itemData.title);
          if (!slug || slug !== correctSlug) {
            // Use navigate instead of window.history.replaceState for hash routing
            navigate(`/marketplace/${id}/${correctSlug}`, { replace: true });
          }
        }

        // DEBUG: Log all payment-related fields
        console.log('🔍 Payment Debug Info:', {
          payment_option: itemData.payment_option,
          payment_id: itemData.payment_id,
          paypal_id: itemData.paypal_id,
          paypal_link: itemData.paypal_link,
          paypal_url: itemData.paypal_url,
          payment_paypal: itemData.payment_paypal,
          paypal_payment_id: itemData.paypal_payment_id,
          venmo_id: itemData.venmo_id,
          venmo_link: itemData.venmo_link,
          venmo_url: itemData.venmo_url,
          payment_venmo: itemData.payment_venmo,
          venmo_payment_id: itemData.venmo_payment_id,
          venmo_username: itemData.venmo_username,
          // Log all keys that contain 'payment', 'paypal', or 'venmo'
          allPaymentFields: Object.keys(itemData).filter(key => 
            key.toLowerCase().includes('payment') || 
            key.toLowerCase().includes('paypal') || 
            key.toLowerCase().includes('venmo')
          ).reduce((acc, key) => {
            acc[key] = itemData[key];
            return acc;
          }, {})
        });
        
        setItem(itemData);
        setCurrentImageIndex(0);
        
        // Check if payment has already been made
        if (itemData.is_paid === 1) {
          toast.success('✅ Payment already completed for this item!', {
            position: "top-center",
            autoClose: 4000,
          });
        }
        
        await checkWishlistStatus(itemData.id);

        // Load submitted offer and status history
        const storedOffer = loadSubmittedOffer(itemData.id);
        if (storedOffer && mounted) {
          setSubmittedOffer(storedOffer);
          
          // Load status history
          try {
            const historyKey = getOfferStatusHistoryKey(itemData.id);
            const history = JSON.parse(safeLocalStorage.getItem(historyKey) || '[]');
            setOfferStatusHistory(history);
          } catch (error) {
            console.warn('Error loading status history:', error);
          }

          // Check stored offer against API data
          if (itemData.buyer_requests && storedOffer.id) {
            const currentOffer = itemData.buyer_requests.find(
              request => parseInt(request.id) === parseInt(storedOffer.id)
            );
            
            if (currentOffer) {
              const apiStatus = normalizeStatus(currentOffer.status);
              const storedStatus = normalizeStatus(storedOffer.status);
              
              if (apiStatus !== storedStatus) {
                const updatedOffer = {
                  ...storedOffer,
                  status: apiStatus,
                  updatedAt: new Date().toISOString()
                };
                
                setSubmittedOffer(updatedOffer);
                saveSubmittedOffer(itemData.id, updatedOffer);
                addStatusToHistory(itemData.id, apiStatus);
                
                // Show notification about the status change
                if (apiStatus === 'accepted') {
                  toast.success('🎉 Your offer was accepted while you were away!', {
                    position: "top-center",
                    autoClose: 5000,
                  });
                } else if (apiStatus === 'rejected') {
                  toast.info('Your offer status has been updated.', {
                    position: "top-center",
                    autoClose: 3000,
                  });
                }
              }
            }
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
          console.error('Error fetching item details:', err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchItemDetails();
    
    return () => {
      mounted = false;
    };
  }, [id, slug, createSlug, navigate, checkWishlistStatus, loadSubmittedOffer, normalizeStatus, saveSubmittedOffer, addStatusToHistory, getOfferStatusHistoryKey]);

  // Validate payment methods when item is loaded
  useEffect(() => {
    if (item) {
      validatePaymentMethods();
    }
  }, [item, validatePaymentMethods]);

  // Polling management
  useEffect(() => {
    if (submittedOffer && isMountedRef.current) {
      const normalizedStatus = normalizeStatus(submittedOffer.status);
      
      if (normalizedStatus === 'pending' && !isPollingStatus) {
        // Add a small delay to ensure component is fully loaded
        const timer = setTimeout(() => {
          if (isMountedRef.current) {
            startStatusPolling();
          }
        }, 1000);
        
        return () => clearTimeout(timer);
      } else if (normalizedStatus !== 'pending' && isPollingStatus) {
        stopStatusPolling();
      }
    }

    return () => {
      // Cleanup polling when component unmounts or dependencies change
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [submittedOffer?.status, submittedOffer?.id, startStatusPolling, stopStatusPolling, isPollingStatus, normalizeStatus]);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      stopStatusPolling();
    };
  }, [stopStatusPolling]);

  // Event handlers
  const toggleWishlist = useCallback(async () => {
    if (!item?.id) return;
    
    const newStatus = !isWishlisted;
    const originalStatus = isWishlisted;
    
    try {
      // Optimistically update the UI
      setIsWishlisted(newStatus);
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Ocp-Apim-Subscription-Key': '6476f278e86f4fc6a4643077bc489c05'
      };
      
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/wishlist`,
        { 
          ad_id: Number(item.id),
          is_wishlisted: newStatus 
        },
        { headers }
      );
      
      toast.success(newStatus ? "Added to wishlist!" : "Removed from wishlist!");
      
    } catch (error) {
      // Revert the UI state on error
      setIsWishlisted(originalStatus);
      console.error('Wishlist error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to manage your wishlist');
      } else if (error.response?.status === 404) {
        toast.error('Item not found');
      } else {
        toast.error('Failed to update wishlist. Please try again.');
      }
    }
  }, [item?.id, isWishlisted]);

  const toggleContactVisibility = useCallback(() => {
    setIsContactVisible(prev => !prev);
  }, []);

  // Chat handler
  const handleChat = useCallback((sellerId) => {
    if (!sellerId || !item) return;
    
    try {
      // Prepare product details to pass to chat
      const productDetails = {
        productName: item.title,
        productPrice: item.price,
        productLink: window.location.href,
        productId: item.id,
        productImage: item.images && item.images.length > 0 ? item.images[0].image_url : '',
        sellerName: item.seller?.name || 'Seller'
      };
      
      // Create URL parameters
      const queryParams = new URLSearchParams({
        user_id: sellerId,
        product_name: productDetails.productName,
        product_price: productDetails.productPrice,
        product_link: productDetails.productLink,
        product_id: productDetails.productId,
        product_image: productDetails.productImage,
        seller_name: productDetails.sellerName,
        auto_message: 'true'
      });
      
      // Navigate to chat with product details
      navigate(`/myaccount/chat?${queryParams.toString()}`);
      
      toast.info('Opening chat with seller...', {
        position: "top-center",
        autoClose: 2000,
      });
      
    } catch (error) {
      console.error('Error navigating to chat:', error);
      toast.error('Failed to open chat. Please try again.', {
        position: "top-center",
        autoClose: 3000,
      });
    }
  }, [item, navigate]);

  // MODIFIED: Simplified handleBuyNow function - directly opens payment modal
  const handleBuyNow = useCallback(async () => {
    setIsLoadingProfile(true);
    
    try {
      // Fetch user profile first and wait for it to complete
      const response = await axiosInstance.get(`${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}/v1/profile`);
      
      let profileData = null;
      if (response.data?.status === 200 && response.data?.data) {
        profileData = {
          name: response.data.data.name || '',
          address: response.data.data.address || '',
          email: response.data.data.email || '',
          phone_number: response.data.data.phone_number || ''
        };
        setUserProfile(profileData);
      }
      
      // Set form with fresh profile data
      setBuyNowForm({
        name: profileData?.name || '',
        pickup_location: item?.shipping_option === 1 
          ? 'Seller will arrange delivery' 
          : (profileData?.address || ''),
        pickup_date: getCurrentDate(),
        pickup_time: getCurrentTime()
      });
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Set form with empty values if profile fetch fails
      setBuyNowForm({
        name: '',
        pickup_location: item?.shipping_option === 1 ? 'Seller will arrange delivery' : '',
        pickup_date: getCurrentDate(),
        pickup_time: getCurrentTime()
      });
    }
    
    setIsLoadingProfile(false);
    
    // Open modal directly at payment method selection
    setIsBuyNowModalOpen(true);
    setSelectedPaymentMethod(null);
    setBuyNowStep('payment-method');
  }, [item?.shipping_option]);

  // Updated handleMakeOffer function with profile fetching
  const handleMakeOffer = useCallback(async () => {
    setIsLoadingProfile(true);
    
    try {
      // Fetch user profile first and wait for it to complete
      const response = await axiosInstance.get(`${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}/v1/profile`);
      
      let profileData = null;
      if (response.data?.status === 200 && response.data?.data) {
        profileData = {
          name: response.data.data.name || '',
          address: response.data.data.address || '',
          email: response.data.data.email || '',
          phone_number: response.data.data.phone_number || ''
        };
        setUserProfile(profileData);
      }
      
      // Set form with fresh profile data
      setOfferForm({
        name: profileData?.name || '',
        pickup_location: item?.shipping_option === 1 
          ? 'Seller will arrange delivery' 
          : (profileData?.address || ''),
        amount: '',
        pickup_date: getCurrentDate(),
        pickup_time: getCurrentTime()
      });
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Set form with empty values if profile fetch fails
      setOfferForm({
        name: '',
        pickup_location: item?.shipping_option === 1 ? 'Seller will arrange delivery' : '',
        amount: '',
        pickup_date: getCurrentDate(),
        pickup_time: getCurrentTime()
      });
    }
    
    setIsLoadingProfile(false);
    
    // Open modal after profile is processed
    setIsOfferModalOpen(true);
  }, [item?.shipping_option]);

  // Buy Now Functions
  const closeBuyNowModal = useCallback(() => {
    setIsBuyNowModalOpen(false);
    setSelectedPaymentMethod(null);
    setIsProcessingPayment(false);
    setShowBuyNowForm(false);
    setShowOnlinePaymentOptions(false);
    setBuyNowStep('payment-method');
    // Reset form with profile data instead of empty values
    setBuyNowForm({
      name: userProfile?.name || '',
      pickup_location: item?.shipping_option === 1 ? 'Seller will arrange delivery' : (userProfile?.address || ''),
      pickup_date: getCurrentDate(),
      pickup_time: getCurrentTime()
    });
    setIsSubmittingBuyNow(false);
  }, [userProfile, item?.shipping_option]);

  const handlePaymentMethodSelect = useCallback((method) => {
    setSelectedPaymentMethod(method);
    setShowBuyNowForm(false);
    setShowOnlinePaymentOptions(false);
  }, []);

  const handleAcceptedOfferPurchase = useCallback(async () => {
    if (!submittedOffer) return;
    
    // For accepted offers, we don't need to send a new buy request
    // The offer is already approved, we just handle payment
    
    if (selectedPaymentMethod === 'online' && !isCashOnlyPayment()) {
      // Go directly to online payment options
      setBuyNowStep('online-options');
      setShowOnlinePaymentOptions(true);
      
      toast.info('Complete your payment using PayPal or Venmo for your accepted offer.', {
        position: "top-center",
        autoClose: 4000,
      });
    } else {
      // For cash payment, the transaction is ready - seller already approved
      closeBuyNowModal();
      
      toast.success('Purchase confirmed! Your offer was already accepted. Contact the seller to arrange cash payment and pickup.', {
        position: "top-center",
        autoClose: 6000,
      });
      
      toast.info('The seller will contact you at the agreed time and location from your original offer.', {
        position: "top-center",
        autoClose: 5000,
      });
    }
  }, [submittedOffer, selectedPaymentMethod, isCashOnlyPayment, closeBuyNowModal]);

  // MODIFIED: Updated handleProceedWithPayment to call payment API for cash purchases
  const handleProceedWithPayment = useCallback(async () => {
    if (!selectedPaymentMethod || !item) return;

    if (isCashOnlyPayment() && selectedPaymentMethod === 'online') {
      toast.error('Only cash payment is available for this item');
      return;
    }

    // Check if this is for an accepted offer
    const hasAcceptedOffer = submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted';
    
    if (hasAcceptedOffer) {
      // For accepted offers, don't send new buy request - just handle payment
      await handleAcceptedOfferPurchase();
    } else {
      // For regular Buy Now purchases
      if (selectedPaymentMethod === 'cash') {
        // For cash payment, mark payment as completed immediately
        try {
          setIsProcessingPayment(true);
          
          const response = await axiosInstance.post(
            `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/paid_status`,
            {
              id: parseInt(item.id),
              is_paid: 1
            }
          );

          if (response.data.status === 200) {
            const updatedItemData = response.data.data;
            
            if (updatedItemData && updatedItemData.is_paid === 1) {
              setItem(prevItem => ({
                ...prevItem,
                is_paid: 1
              }));

              closeBuyNowModal();
              
              toast.success('Purchase confirmed! Payment will be made in cash when you receive the item.', {
                position: "top-center",
                autoClose: 5000,
              });

              toast.info('Use the "Chat with Seller" button to coordinate pickup details and timing.', {
                position: "top-center",
                autoClose: 5000,
              });
            } else {
              toast.warning('Purchase request sent, but confirmation pending. Please contact seller if needed.', {
                position: "top-center",
                autoClose: 5000,
              });
              closeBuyNowModal();
            }
          } else {
            throw new Error(response.data.message || 'Failed to complete purchase');
          }
        } catch (error) {
          console.error('Error completing cash purchase:', error);
          
          let errorMessage = 'Failed to complete cash purchase';
          
          if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response?.data?.errors) {
            errorMessage = error.response.data.errors.join(', ');
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          toast.error(errorMessage, {
            position: "top-center",
            autoClose: 5000,
          });
        } finally {
          setIsProcessingPayment(false);
        }
      } else if (selectedPaymentMethod === 'online' && !isCashOnlyPayment()) {
        // For online payment, go to payment options
        setBuyNowStep('online-options');
        setShowOnlinePaymentOptions(true);
        
        toast.success('Ready for payment! Complete your purchase using PayPal or Venmo below.', {
          position: "top-center",
          autoClose: 4000,
        });
      }
    }
  }, [selectedPaymentMethod, item, submittedOffer, isCashOnlyPayment, normalizeStatus, handleAcceptedOfferPurchase, closeBuyNowModal]);

  const handlePaymentCompleted = useCallback(async () => {
    if (!item?.id) {
      toast.error('Unable to mark payment as completed. Item ID not found.', {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsProcessingPayment(true);
      
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/paid_status`,
        {
          id: parseInt(item.id),
          is_paid: 1
        }
      );

      if (response.data.status === 200) {
        const updatedItemData = response.data.data;
        
        if (updatedItemData && updatedItemData.is_paid === 1) {
          setItem(prevItem => ({
            ...prevItem,
            is_paid: 1
          }));

          closeBuyNowModal();
          
          toast.success('🎉 Payment successfully recorded! The seller has been notified and will process your order.', {
            position: "top-center",
            autoClose: 5000,
          });

          toast.info('You can check your order status by contacting the seller.', {
            position: "top-center",
            autoClose: 4000,
          });
        } else {
          toast.warning('Payment status update sent, but confirmation pending. Please contact seller if needed.', {
            position: "top-center",
            autoClose: 5000,
          });
          closeBuyNowModal();
        }
      } else {
        throw new Error(response.data.message || 'Failed to update payment status');
      }
    } catch (error) {
      console.error('❌ Error marking payment as completed:', error);
      
      let errorMessage = 'Failed to mark payment as completed';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [item?.id, closeBuyNowModal]);

const handleBuyNowFormChange = useCallback((e) => {
  const { name, value } = e.target;
  
  setBuyNowForm(prev => {
    const newForm = { ...prev, [name]: value };
    
    // If changing time, validate it's not in the past
    if (name === 'pickup_time' && newForm.pickup_date) {
      const minTime = getMinimumTime(newForm.pickup_date);
      if (minTime && value < minTime) {
        toast.error('Please select a future time', {
          position: "top-center",
          autoClose: 2000,
        });
        return prev; // Don't update if time is invalid
      }
    }
    
    return newForm;
  });
}, [getMinimumTime]);

  const handleOfferFormChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setOfferForm(prev => {
      const newForm = { ...prev, [name]: value };
      
      // If changing time, validate it's not in the past
      if (name === 'pickup_time' && newForm.pickup_date) {
        const minTime = getMinimumTime(newForm.pickup_date);
        if (minTime && value < minTime) {
          toast.error('Please select a future time', {
            position: "top-center",
            autoClose: 2000,
          });
          return prev; // Don't update if time is invalid
        }
      }
      
      return newForm;
    });
  }, [getMinimumTime]);

// Validate and reset Buy Now form time when date changes
useEffect(() => {
  if (buyNowForm.pickup_date && buyNowForm.pickup_time) {
    const minTime = getMinimumTime(buyNowForm.pickup_date);
    if (minTime && buyNowForm.pickup_time < minTime) {
      setBuyNowForm(prev => ({
        ...prev,
        pickup_time: minTime
      }));
    }
  }
}, [buyNowForm.pickup_date, getMinimumTime]);

// Validate and reset Offer form time when date changes
useEffect(() => {
  if (offerForm.pickup_date && offerForm.pickup_time) {
    const minTime = getMinimumTime(offerForm.pickup_date);
    if (minTime && offerForm.pickup_time < minTime) {
      setOfferForm(prev => ({
        ...prev,
        pickup_time: minTime
      }));
    }
  }
}, [offerForm.pickup_date, getMinimumTime]);

  const closeOfferModal = useCallback(() => {
    setIsOfferModalOpen(false);
    // Reset form with profile data instead of empty values
    setOfferForm({
      name: userProfile?.name || '',
      pickup_location: item?.shipping_option === 1 ? 'Seller will arrange delivery' : (userProfile?.address || ''),
      amount: '',
      pickup_date: getCurrentDate(),
      pickup_time: getCurrentTime()
    });
  }, [userProfile, item?.shipping_option]);

  const handleOfferSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!id) return;
    
    setIsSubmittingOffer(true);

    try {
      // Validate that time is not in the past for today's date
      if (offerForm.pickup_date === getCurrentDate()) {
        const selectedDateTime = new Date(`${offerForm.pickup_date}T${offerForm.pickup_time}`);
        const now = new Date();
        
        if (selectedDateTime <= now) {
          toast.error('Please select a future time for today\'s date', {
            position: "top-center",
            autoClose: 3000,
          });
          setIsSubmittingOffer(false);
          return;
        }
      }

      const offerData = {
        ad_id: parseInt(id),
        name: offerForm.name.trim(),
        pickup_location: item.shipping_option === 1 ? 'Seller will arrange delivery' : offerForm.pickup_location.trim(),
        amount: parseFloat(offerForm.amount),
        pickup_date: offerForm.pickup_date,
        pickup_time: offerForm.pickup_time,
        payment_mode: 1  // Offers are always cash only
      };

      // Validation
      if (!offerData.name || 
          (item.shipping_option !== 1 && !offerData.pickup_location) || 
          !offerData.amount || 
          offerData.amount <= 0) {
        throw new Error('Please fill in all required fields with valid values');
      }

   if (offerData.amount >= item.price) {
  const shouldProceed = await new Promise((resolve) => {
    toast.warn(
      <div>
        <p>Your offer of $ {offerData.amount} is equal to or higher than the asking price of $ {item.price}. Are you sure you want to proceed with this offer?</p>
        <div className="mt-3 flex space-x-2">
          <button
            onClick={() => {
              toast.dismiss();
              resolve(true);
            }}
            className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600"
          >
            Yes, Proceed
          </button>
          <button
            onClick={() => {
              toast.dismiss();
              resolve(false);
            }}
            className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        closeButton: false,
        draggable: false,
      }
    );
  });
  
  if (!shouldProceed) {
    setIsSubmittingOffer(false);
    return;
  }
}

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/orders/buy_request`,
        offerData
      );

      if (response.data.status === 200) {
        const responseData = response.data.data;
        
        toast.success(response.data.message || 'Offer submitted successfully!', {
          position: "top-center",
          autoClose: 3000,
        });
        
        const submittedOfferDetails = {
          id: responseData.id,
          ad_id: responseData.ad_id,
          student_id: responseData.student_id,
          name: responseData.name,
          pickup_location: responseData.pickup_location,
          amount: responseData.amount,
          pickup_date: responseData.pickup_date,
          pickup_time: responseData.pickup_time,
          payment_mode: responseData.payment_mode,
          status: normalizeStatus(responseData.status) || 'pending',
          submittedAt: responseData.created_at,
          updatedAt: responseData.updated_at
        };
        
        setSubmittedOffer(submittedOfferDetails);
        saveSubmittedOffer(parseInt(id), submittedOfferDetails);
        addStatusToHistory(parseInt(id), 'pending', responseData.created_at);
        closeOfferModal();
        
        // Start polling for status updates
        setTimeout(() => {
          if (isMountedRef.current) {
            startStatusPolling();
          }
        }, 2000);
        
        toast.info('You will be notified when the seller responds to your offer.', {
          position: "top-center",
          autoClose: 5000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to submit offer');
      }
    } catch (error) {
      console.error('❌ Error submitting offer:', error);
      
      let errorMessage = 'Failed to submit offer';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSubmittingOffer(false);
    }
  }, [id, item, offerForm, normalizeStatus, saveSubmittedOffer, addStatusToHistory, startStatusPolling, closeOfferModal]);

  const handleBuyerRequestAction = useCallback(async (requestId, action) => {
    if (!requestId || !action) return;
    
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/orders/buyer_request/${requestId}`,
        { status: action }
      );

      if (response.data.status === 200) {
        toast.success(`Request ${action}ed successfully!`);
        
        setTimeout(async () => {
          try {
            const itemResponse = await axiosInstance.get(
              `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/detail/${id}`
            );
            
            if (itemResponse.data.status === 200 && isMountedRef.current) {
              setItem(itemResponse.data.data);
            }
          } catch (error) {
            console.error('❌ Error refreshing item details:', error);
          }
        }, 1000);
      } else {
        throw new Error(response.data.message || `Failed to ${action} request`);
      }
    } catch (error) {
      console.error(`❌ Error ${action}ing request:`, error);
      toast.error(`Failed to ${action} request`);
    }
  }, [id]);

  const clearSubmittedOffer = useCallback(() => {
    if (item?.id) {
      setSubmittedOffer(null);
      setOfferStatusHistory([]);
      removeSubmittedOffer(item.id);
      stopStatusPolling();
    }
  }, [item?.id, removeSubmittedOffer, stopStatusPolling]);

  // Utility functions
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }, []);

  const formatTime = useCallback((timeString) => {
    if (!timeString) return 'Not specified';
    try {
      const [hours, minutes] = timeString.split(':');
      const time = new Date();
      time.setHours(parseInt(hours), parseInt(minutes));
      return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (normalizeStatus(status)) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  }, [normalizeStatus]);

  const getStatusIcon = useCallback((status) => {
    switch (normalizeStatus(status)) {
      case 'accepted':
        return <FaCheckCircle className="text-green-500" />;
      case 'rejected':
        return <FaExclamationCircle className="text-red-500" />;
      case 'pending':
      default:
        return <FaClock className="text-yellow-500" />;
    }
  }, [normalizeStatus]);

  // Early returns for loading/error states
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <img 
          src={loadingGif} 
          alt="Loading..." 
          style={{ width: '80px' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
          <Link to="/marketplace" className="mt-2 inline-block text-blue-600 hover:text-blue-800">
            ← Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Not Found</strong>
          <span className="block sm:inline"> Item with ID {id} not found.</span>
          <Link to="/marketplace" className="mt-2 inline-block text-blue-600 hover:text-blue-800">
            ← Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  const currentImage = item.images && item.images.length > 0 ? item.images[currentImageIndex] : null;
  const availablePaymentMethods = getAvailablePaymentMethods();

  return (
    <div className="market-details container mx-auto px-4 py-8">
      <Link to="/marketplace" className="inline-flex items-center mb-6 text-blue-600 hover:text-blue-800">
        <FaChevronLeft className="mr-2" /> Back to Marketplace
      </Link>

      {/* Payment Completed Banner */}
      {item.is_paid === 1 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <FaCheckCircle className="text-blue-500 text-xl mr-3" />
            <div className="flex-1">
              <h3 className="text-blue-800 font-semibold">✅ Payment Completed Successfully!</h3>
              <p className="text-blue-700 text-sm mt-1">
                Your payment for this item has been recorded. The seller has been notified and will process your order soon.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status notification banner for approved offers */}
      {submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted' && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <FaCheckCircle className="text-green-500 text-xl mr-3" />
            <div className="flex-1">
              <h3 className="text-green-800 font-semibold">🎉 Congratulations! Your offer has been accepted!</h3>
              <p className="text-green-700 text-sm mt-1">
                The seller has approved your offer of $ {submittedOffer.amount}. They should contact you soon to arrange the transaction.
              </p>
            </div>
            {isPollingStatus && (
              <FaBell className="text-green-500 animate-pulse ml-2" />
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="inline-block px-3 py-1 text-sm font-semibold text-white site-bg rounded-full mb-2">
                    {item.category_name || 'Uncategorized'}
                  </span>
                  <h1 className="text-3xl font-bold text-gray-900">{item.title}</h1>
                </div>
               <button
  onClick={toggleWishlist}
  className="text-2xl focus:outline-none"
  aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
>
  {isWishlisted || item?.is_wishlisted ? (
    <FaHeart className="text-site-500" />
  ) : (
    <FaRegHeart className="text-gray-400 hover:text-site-500" />
  )}
</button>
              </div>

              <div className="flex items-center text-sm text-gray-500 mb-6">
                <span className="flex items-center mr-4">
                 <FaCalendarAlt className="w-3 h-3 mr-1" style={{ color: '#9e9e9eff' }} />
                  Posted {item.posted_on || 'Unknown'} &nbsp; &nbsp;&nbsp;&nbsp;&nbsp;
                  👁 Views: {item.views || 0} 
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 512 512">
                    <path d="M393,450a14.92,14.92,0,0,1-7.46-2L257,374.29,128.46,448A15,15,0,0,1,106,435V63a15,15,0,0,1,15-15H393a15,15,0,0,1,15,15V435a15,15,0,0,1-15,15ZM257,342a14.92,14.92,0,0,1,7.46,2L378,409.1V78H136V409.1L249.54,344A14.92,14.92,0,0,1,257,342Z" />
                  </svg>
                  ID: #{id}
                </span>
              </div>

              {/* Image Gallery */}
              <div className="relative mb-6">
                {currentImage ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <img
                        src={currentImage.image_url}
                        alt={item.title}
                        className="w-full h-96 object-contain rounded-lg bg-gray-100"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-96 bg-gray-100 rounded-lg hidden items-center justify-center text-gray-500">
                        Image not available
                      </div>

                      <button
                        onClick={() => setIsZoomOpen(true)}
                        className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                        aria-label="Zoom Image"
                      >
                        <FaSearchPlus />
                      </button>

                      {item.images && item.images.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                          >
                            <FaChevronRight />
                          </button>
                        </>
                      )}
                    </div>
                    {item.images && item.images.length > 1 && (
                      <div className="flex justify-center space-x-2">
                        {item.images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => goToImage(index)}
                            className={`w-3 h-3 rounded-full ${currentImageIndex === index ? 'bg-blue-500' : 'bg-gray-300'}`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                    No image available
                  </div>
                )}
              </div>

              {/* Zoom Modal */}
              {isZoomOpen && currentImage && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50">
                  <div className="relative max-w-4xl w-full p-4 flex justify-center items-center">
                    <button
                      onClick={() => setIsZoomOpen(false)}
                      className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300"
                    >
                      <FaTimes />
                    </button>

                    {item.images && item.images.length > 1 && (
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                      >
                        <FaChevronLeft />
                      </button>
                    )}

                    <img
                      src={currentImage.image_url}
                      alt={item.title}
                      className="w-full max-h-[80vh] object-contain mx-auto"
                    />

                    {item.images && item.images.length > 1 && (
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition"
                      >
                        <FaChevronRight />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Description Section */}
              <div className="mb-8">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Description</h2>
                <p className="text-gray-700 whitespace-pre-line">{item.description || 'No description available'}</p>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className='p-6'>
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Item Details</h3>
              <ul className="space-y-2">
                <li className="flex justify-between">
                  <span className="text-gray-600">Condition</span>
                  <span className="font-medium">{getItemCondition(item)}</span>
                </li>
                
                <li className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`font-medium ${item.is_sold ? 'text-red-500' : 'text-green-500'}`}>
                    {item.is_sold ? 'Sold' : 'Available'}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Offers Received Section - Only show to seller */}
          {item.buyer_requests && item.buyer_requests.length > 0 && isCurrentUserSeller() && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <FaUsers className="mr-2" />
                    Offers Received ({item.buyer_requests.length})
                  </h3>
                </div>

                <div className="space-y-4">
                  {item.buyer_requests.map((request, index) => (
                    <div key={request.id || index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">Request #{request.id || index + 1}</h4>
                          <p className="text-sm text-gray-500">{request.requested_at || 'Unknown date'}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {normalizeStatus(request.status).charAt(0).toUpperCase() + normalizeStatus(request.status).slice(1)}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                          <span className="text-sm text-gray-600">Offer Amount:</span>
                          <p className="font-bold text-lg text-green-600">$ {request.amount || 0}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Payment Mode:</span>
                          <p className="font-medium">{request.payment_mode || 'Cash only'}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">
                            {item.shipping_option === 1 ? 'Delivery Location:' : 'Pickup Location:'}
                          </span>
                          <p className="font-medium">{request.pickup_location || 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">
                            {item.shipping_option === 1 ? 'Delivery Date:' : 'Pickup Date:'}
                          </span>
                          <p className="font-medium">{formatDate(request.pickup_date)}</p>
                        </div>
                        <div className="md:col-span-2">
                          <span className="text-sm text-gray-600">
                            {item.shipping_option === 1 ? 'Delivery Time:' : 'Pickup Time:'}
                          </span>
                          <p className="font-medium">{formatTime(request.pickup_time)}</p>
                        </div>
                      </div>

                      {normalizeStatus(request.status) === 'pending' && currentUser && isCurrentUserSeller() && (
                        <div className="flex space-x-2 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => handleBuyerRequestAction(request.id, 'accept')}
                            className="flex-1 bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition text-sm font-medium"
                          >
                            Accept Offer
                          </button>
                          <button
                            onClick={() => handleBuyerRequestAction(request.id, 'reject')}
                            className="flex-1 bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition text-sm font-medium"
                          >
                            Reject Offer
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Seller Info and Actions */}
        <div className="lg:col-span-1">
          <div className="right-price-op relative sitebg from-purple-500 to-pink-500 rounded-2xl shadow-xl text-white sticky top-4 overflow-hidden p-6 space-y-4">
            <div className="absolute top-2 right-2 bg-yellow-300 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full shadow">
              Best Deal
            </div>

            <div className="text-center">
              <h2 className="text-5xl font-extrabold">$ {item.price || 0}</h2>
              <p className="text-sm mt-1 opacity-80">Hurry, limited availability!</p>
              
              {/* Pickup Location Display */}
              {item.location && (
                <div className="mt-3 bg-white bg-opacity-20 rounded-lg p-2">
                  <div className="flex items-center justify-center text-sm">
                    <FaMapMarkerAlt className="mr-2 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-600 opacity-90">Pickup Location</p>
                      <p className="font-medium text-gray-800">{item.location}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!item.is_sold && !isCurrentUserSeller() && item.is_paid !== 1 ? (
              <div className="space-y-3 pt-4">
                <button
                  onClick={() => handleChat(item.seller?.id)}
                  className="w-full bg-white text-purple-600 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100 transition"
                  disabled={!item.seller?.id}
                >
                  Chat with Seller
                </button>
                
                {!hasSubmittedOffer() ? (
                  <>
                    <button
                      onClick={handleMakeOffer}
                      className="w-full bg-yellow-300 text-black font-semibold py-2 px-4 rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={isLoadingProfile}
                    >
                      {isLoadingProfile ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        'Make an Offer'
                      )}
                    </button>
                    
                    <button
                      onClick={handleBuyNow}
                      className="buy-n w-full border border-white text-white font-semibold py-2 px-4 rounded-lg hover:bg-white hover:text-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={isLoadingProfile}
                    >
                      {isLoadingProfile ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        'Buy Now'
                      )}
                    </button>
                  </>
                ) : (
                  <div className="text-center bg-green-100 text-green-800 p-3 rounded-lg">
                    <div className="flex items-center justify-center mb-2">
                      {getStatusIcon(submittedOffer?.status)}
                      <span className="font-semibold ml-2">
                        {normalizeStatus(submittedOffer?.status) === 'pending' && (
                          submittedOffer?.amount === item?.price ? 'Buy Request Submitted - Pending' : 'Offer Submitted - Pending'
                        )}
                        {normalizeStatus(submittedOffer?.status) === 'accepted' && (
                          submittedOffer?.amount === item?.price ? 'Buy Request Accepted ✓' : 'Offer Accepted ✓'
                        )}
                        {normalizeStatus(submittedOffer?.status) === 'rejected' && (
                          submittedOffer?.amount === item?.price ? 'Buy Request Rejected' : 'Offer Rejected'
                        )}
                        {!submittedOffer?.status && (
                          submittedOffer?.amount === item?.price ? 'Buy Request Submitted' : 'Offer Submitted'
                        )}
                      </span>
                    </div>
                    
                    {normalizeStatus(submittedOffer?.status) === 'pending' && (
                      <div className="flex items-center justify-center text-xs text-green-600 mb-2">
                        <FaBell className="animate-pulse mr-1" />
                        <span>Checking for updates...</span>
                        <button
                          onClick={manualRefreshStatus}
                          className="ml-2 text-blue-600 hover:text-blue-800 underline"
                          title="Check now"
                        >
                          Refresh
                        </button>
                      </div>
                    )}
                    
                    {normalizeStatus(submittedOffer?.status) === 'accepted' && (
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-green-800 font-medium text-sm">
                            🎉 Your offer of $ {submittedOffer.amount} has been accepted!
                          </p>
                          <p className="text-green-700 text-xs mt-1">
                            Complete your purchase to finalize the transaction.
                          </p>
                        </div>
                        <button
                          onClick={handleBuyNow}
                          className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          disabled={isLoadingProfile}
                        >
                          {isLoadingProfile ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Loading...
                            </>
                          ) : (
                            `Buy Now for $ ${submittedOffer.amount}`
                          )}
                        </button>
                      </div>
                    )}
                    
                    {normalizeStatus(submittedOffer?.status) === 'rejected' && (
                      <div className="space-y-2">
                        <button
                          onClick={clearSubmittedOffer}
                          className="block w-full text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                          {submittedOffer?.amount === item?.price ? 'Make a new buy request' : 'Make a new offer'}
                        </button>
                        <div className="text-xs text-gray-600">
                          You can try {submittedOffer?.amount === item?.price ? 'making an offer for a different amount' : 'buying at full price'} instead
                        </div>
                      </div>
                    )}
                    
                    {normalizeStatus(submittedOffer?.status) === 'pending' && (
                      <p className="text-xs mt-2">
                        The seller will review your {submittedOffer?.amount === item?.price ? 'buy request' : 'offer'}.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : item.is_paid === 1 ? (
              <div className="text-center bg-blue-100 text-blue-800 p-3 rounded-lg pt-4">
                <div className="flex items-center justify-center mb-2">
                  <FaCheckCircle className="text-blue-600 mr-2" />
                  <span className="font-semibold">Payment Completed</span>
                </div>
                <p className="text-sm">
                  Your payment has been recorded. The seller will contact you soon to arrange delivery/pickup.
                </p>
              </div>
            ) : item.is_sold ? (
              <div className="text-center text-yellow-200 font-semibold pt-4">Item Sold</div>
            ) : (
              <div className="text-center text-yellow-200 font-semibold pt-4">Your Item</div>
            )}
          </div>

          {/* Seller Information */}
          <div className='bg-white rounded-lg shadow-md overflow-hidden sticky top-4 mt-3'>
            <div className='p-6'>
              <div className="">
                {!item.is_sold && !isCurrentUserSeller() && (
                  <button
                    onClick={toggleContactVisibility}
                    className=" float-end text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    {isContactVisible ? 'Hide Contact Info' : 'Show Contact Info'}
                  </button>
                )}
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Seller Information</h3>
                <div className="flex items-start space-x-4 mb-4">
                  <div className="flex-shrink-0">
                    <img
                      src={item.seller?.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.seller?.name || 'User')}&background=6366f1&color=ffffff&size=48&rounded=true`}
                      alt={item.seller?.name || 'Seller'}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.seller?.name || 'User')}&background=6366f1&color=ffffff&size=48&rounded=true`;
                      }}
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{item.seller?.name || 'Unknown Seller'}</p>
                    <p className="text-sm text-gray-500">Member since {item.seller?.member_since || 'Unknown'}</p>
                    {item.seller?.rating && (
                      <div className="flex items-center mt-1">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className={`w-4 h-4 ${i < Math.floor(item.seller.rating) ? 'fill-current' : 'stroke-current'}`}
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 ml-1">{item.seller.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {isContactVisible && item.seller && (
                  <div className="space-y-3 mt-4">
                    {item.seller.phone_number && (
                      <div className="flex items-center text-gray-700">
                        <FaPhone className="text-gray-500 mr-2" />
                        <a href={`tel:${item.seller.phone_number}`} className="hover:text-blue-600">
                          {item.seller.phone_number}
                        </a>
                      </div>
                    )}
                    {item.seller.email && (
                      <div className="flex items-center text-gray-700">
                        <FaEnvelope className="text-gray-500 mr-2" />
                        <a href={`mailto:${item.seller.email}`} className="hover:text-blue-600">
                          {item.seller.email}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Safety Tips */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Safety Tips</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Meet in a public place
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Check the item before buying
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Never pay in advance
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 mt-1 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Use secure payment methods
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Submitted Offer Details Section */}
      {submittedOffer && (
        <div className="mt-8 bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-green-500">
          <div className="p-6">
            <div className="flex items-center mb-4">
              {getStatusIcon(submittedOffer.status)}
              <h3 className="text-xl font-bold text-gray-800 ml-2">Your Buy Request</h3>
              <span className={`ml-auto text-xs font-medium px-2.5 py-1 rounded-full flex items-center ${getStatusColor(submittedOffer.status)}`}>
                {normalizeStatus(submittedOffer.status) === 'pending' && <FaClock className="mr-1" />}
                {normalizeStatus(submittedOffer.status) === 'accepted' && <FaCheckCircle className="mr-1" />}
                {normalizeStatus(submittedOffer.status) === 'rejected' && <FaTimes className="mr-1" />}
                {normalizeStatus(submittedOffer.status).charAt(0).toUpperCase() + normalizeStatus(submittedOffer.status).slice(1)}
              </span>
              {normalizeStatus(submittedOffer.status) === 'pending' && (
                <button
                  onClick={manualRefreshStatus}
                  className="ml-2 text-blue-500 hover:text-blue-700 p-1 rounded transition-colors"
                  title="Check for status updates"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
              {normalizeStatus(submittedOffer.status) === 'pending' && isPollingStatus && (
                <FaBell className="text-blue-500 animate-pulse ml-2" title="Auto-checking for updates" />
              )}
            </div>
            
            {/* Enhanced status messaging based on approval */}
            {normalizeStatus(submittedOffer.status) === 'accepted' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center text-green-800">
                  <FaCheckCircle className="mr-2" />
                  <strong>Excellent! Your offer has been approved by the seller.</strong>
                </div>
                <p className="text-green-700 text-sm mt-1">
                  🎉 The seller has accepted your offer of $ {submittedOffer.amount}. They should contact you soon at the details you provided to arrange the {item.shipping_option === 1 ? 'delivery' : 'pickup'}.
                </p>
              </div>
            )}
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Request ID</label>
                  <p className="text-gray-900 font-mono text-sm">#{submittedOffer.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Your Name</label>
                  <p className="text-gray-900 font-medium">{submittedOffer.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Offer Amount</label>
                  <p className="text-gray-900 font-bold text-lg text-green-600">$ {submittedOffer.amount}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Payment Method</label>
                  <p className="text-gray-900">{submittedOffer.payment_mode === 1 ? 'Cash Only' : 'Online Payment'}</p>
                </div>
                {item.shipping_option !== 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      {item.shipping_option === 1 ? 'Delivery Location' : 'Pickup Location'}
                    </label>
                    <p className="text-gray-900">{submittedOffer.pickup_location}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    {item.shipping_option === 1 ? 'Delivery Date & Time' : 'Pickup Date & Time'}
                  </label>
                  <p className="text-gray-900">
                    {formatDate(submittedOffer.pickup_date)} at {formatTime(submittedOffer.pickup_time)}
                  </p>
                </div>
              </div>
              
              <div className="pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Request Submitted:</strong> {new Date(submittedOffer.submittedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
                {submittedOffer.updatedAt && submittedOffer.updatedAt !== submittedOffer.submittedAt && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Last Updated:</strong> {new Date(submittedOffer.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </p>
                )}
                
                {normalizeStatus(submittedOffer.status) === 'pending' && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      The seller will review your offer and contact you if interested. You will be notified of any updates.
                    </p>
                    {isPollingStatus && (
                      <p className="text-xs text-blue-600 mt-1 flex items-center">
                        <FaBell className="animate-pulse mr-1" />
                        Automatically checking for status updates...
                      </p>
                    )}
                  </div>
                )}
                
                {normalizeStatus(submittedOffer.status) === 'accepted' && (
                  <div className="mt-2 p-2 bg-green-100 rounded border-l-4 border-green-400">
                    <p className="text-sm text-green-800 font-medium">
                      🎉 Your offer was approved! The seller should contact you soon to arrange the transaction at the location and time you specified.
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Make sure to check your contact details and be available at the scheduled time.
                    </p>
                  </div>
                )}
                
                {normalizeStatus(submittedOffer.status) === 'rejected' && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600 font-medium">
                      Your offer was not accepted by the seller.
                    </p>
                    <button
                      onClick={clearSubmittedOffer}
                      className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                    >
                      Make a new offer
                    </button>
                  </div>
                )}
              </div>

              {/* Status History */}
              {offerStatusHistory.length > 1 && (
                <div className="pt-3 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Status History</h4>
                  <div className="space-y-1">
                    {offerStatusHistory.map((historyItem, index) => (
                      <div key={historyItem.id || index} className="flex items-center text-xs text-gray-600">
                        {getStatusIcon(historyItem.status)}
                        <span className="ml-2">
                          {historyItem.status.charAt(0).toUpperCase() + historyItem.status.slice(1)} - {' '}
                          {new Date(historyItem.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODIFIED: Simplified Buy Now Payment Modal - No form step */}
      {isBuyNowModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {buyNowStep === 'online-options' ? 'Complete Payment' : 'Buy Now - Choose Payment'}
                </h3>
                <button
                  onClick={closeBuyNowModal}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isProcessingPayment}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mb-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-gray-800">{item.title}</h4>
                  <div className="flex items-center space-x-2">
                    {submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted' ? (
                      <>
                        <p className="text-2xl font-bold text-green-600">$ {submittedOffer.amount}</p>
                        <span className="text-sm text-gray-500 line-through">$ {item.price}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Accepted Offer</span>
                      </>
                    ) : (
                      <p className="text-2xl font-bold text-green-600">$ {item.price}</p>
                    )}
                  </div>
                </div>

                {buyNowStep === 'payment-method' ? (
                  <>
                    {/* Show message for accepted offers */}
                    {submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted' && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center text-green-800">
                          <FaCheckCircle className="mr-2" />
                          <strong>Purchasing Your Accepted Offer</strong>
                        </div>
                        <p className="text-green-700 text-sm mt-1">
                          Your offer of $ {submittedOffer.amount} was accepted! Complete your payment to finalize the transaction.
                        </p>
                      </div>
                    )}

                    <p className="text-sm text-gray-600 mb-4">
                      Select your payment method to complete this purchase:
                    </p>

                    <div className="space-y-3">
                      {/* Cash Payment Option */}
                      <div
                        onClick={() => handlePaymentMethodSelect('cash')}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          selectedPaymentMethod === 'cash'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded-full mr-3 ${
                            selectedPaymentMethod === 'cash' ? 'bg-green-500' : 'bg-gray-300'
                          }`}>
                            {selectedPaymentMethod === 'cash' && (
                              <div className="w-full h-full rounded-full bg-green-500 flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center">
                            <FaMoneyBillWave className="text-green-600 mr-2" />
                            <div>
                              <h5 className="font-medium text-gray-900">Cash Payment</h5>
                              <p className="text-sm text-gray-600">Pay the seller directly when you receive the item</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Online Payment Option */}
                      {!isCashOnlyPayment() && (
                        <div
                          onClick={() => handlePaymentMethodSelect('online')}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                            selectedPaymentMethod === 'online'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-4 h-4 rounded-full mr-3 ${
                              selectedPaymentMethod === 'online' ? 'bg-blue-500' : 'bg-gray-300'
                            }`}>
                              {selectedPaymentMethod === 'online' && (
                                <div className="w-full h-full rounded-full bg-blue-500 flex items-center justify-center">
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center">
                              <FaCreditCard className="text-blue-600 mr-2" />
                              <div>
                                <h5 className="font-medium text-gray-900">Online Payment</h5>
                                <p className="text-sm text-gray-600">Pay securely using PayPal or Venmo</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Show message if only cash is available */}
                      {isCashOnlyPayment() && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center text-yellow-800">
                            <FaMoneyBillWave className="mr-2" />
                            <span className="text-sm font-medium">Cash Only Payment</span>
                          </div>
                          <p className="text-yellow-700 text-xs mt-1">
                            This seller only accepts cash payments. You'll pay directly when you receive the item.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // Online payment options step
                  <>
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-800">
                        <FaCheckCircle className="mr-2" />
                        <strong>Ready to Purchase!</strong>
                      </div>
                      <p className="text-green-700 text-sm mt-1">
                        Complete your payment to finalize this purchase.
                      </p>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                      Complete your payment:
                    </p>

                    <div className="space-y-3">
                      {availablePaymentMethods.includes('paypal') && (
                        <button
                          onClick={handlePayPalPayment}
                          disabled={isProcessingPayment}
                          className="w-full border-2 border-gray-200 hover:border-blue-500 rounded-lg p-4 transition-all hover:bg-blue-50 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center">
                            <FaPaypal className="text-blue-600 text-2xl mr-3" />
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">Pay with PayPal</h5>
                              <p className="text-sm text-gray-600">Secure payment via PayPal</p>
                            </div>
                            <div className="ml-auto">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </button>
                      )}

                      {availablePaymentMethods.includes('venmo') && (
                        <button
                          onClick={handleVenmoPayment}
                          disabled={isProcessingPayment}
                          className="w-full border-2 border-gray-200 hover:border-purple-500 rounded-lg p-4 transition-all hover:bg-purple-50 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center">
                            <FaMobileAlt className="text-purple-500 text-2xl mr-3" />
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">Pay with Venmo</h5>
                              <p className="text-sm text-gray-600">Quick payment via Venmo</p>
                            </div>
                            <div className="ml-auto">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </button>
                      )}

                      {availablePaymentMethods.length === 0 && (
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <p className="text-gray-600">No payment methods configured by seller</p>
                          <p className="text-sm text-gray-500 mt-1">Please contact the seller for payment instructions</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800 text-sm">
                        <strong>Note:</strong> After completing the payment, click "Payment Completed" below to notify the seller.
                      </p>
                    </div>

                    {/* Payment Completed Button */}
                    <div className="mt-4">
                      <button
                        onClick={handlePaymentCompleted}
                        disabled={isProcessingPayment}
                        className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isProcessingPayment ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Confirming Payment...
                          </>
                        ) : (
                          'Payment Completed'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Updated button section */}
              <div className="flex space-x-3">
                {buyNowStep === 'payment-method' ? (
                  <>
                    <button
                      type="button"
                      onClick={closeBuyNowModal}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                      disabled={isProcessingPayment}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProceedWithPayment}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={!selectedPaymentMethod || isProcessingPayment}
                    >
                      {isProcessingPayment ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        selectedPaymentMethod === 'cash' ? 'Complete Cash Purchase' : 'Proceed to Payment'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setBuyNowStep('payment-method')}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                    >
                      Back
                    </button>
                  </>
                )}
              </div>

              {selectedPaymentMethod && buyNowStep === 'payment-method' && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    {submittedOffer && normalizeStatus(submittedOffer.status) === 'accepted' ? (
                      selectedPaymentMethod === 'cash' 
                        ? "Your accepted offer will be completed with cash payment when you receive the item."
                        : "Your accepted offer will be completed with secure online payment."
                    ) : (
                      selectedPaymentMethod === 'cash' 
                        ? "You'll pay the seller directly in cash when you receive the item. Use chat to coordinate pickup details."
                        : "You'll pay securely using PayPal or Venmo to complete this purchase immediately."
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Make an Offer Modal */}
      {isOfferModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Make an Offer</h3>
                <button
                  onClick={closeOfferModal}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isSubmittingOffer}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleOfferSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={offerForm.name}
                    onChange={handleOfferFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your full name"
                    disabled={isSubmittingOffer}
                  />
                </div>

                {!isCurrentUserSeller() && item.shipping_option !== 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.shipping_option === 2 ? 'Delivery Location *' : 'Pickup Location *'}
                    </label>
                    <input
                      type="text"
                      name="pickup_location"
                      value={offerForm.pickup_location}
                      onChange={handleOfferFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder={item.shipping_option === 2 ? 'Enter delivery address' : 'Enter pickup location'}
                      disabled={isSubmittingOffer}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offer Amount ($) *
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={offerForm.amount}
                    onChange={handleOfferFormChange}
                    required
                    min="1"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={`Original price: $ ${item.price}`}
                    disabled={isSubmittingOffer}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.shipping_option === 1 ? 'Delivery Date *' : 'Pickup Date *'}
                    </label>
                    <input
                      type="date"
                      name="pickup_date"
                      value={offerForm.pickup_date}
                      onChange={handleOfferFormChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                      disabled={isSubmittingOffer}
                      onKeyDown={(e) => {
                        // Prevent manual typing but allow picker navigation
                        if (e.key !== 'Tab' && e.key !== 'Escape' && e.key !== 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => e.preventDefault()}
                      style={{
                        colorScheme: 'light',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {item.shipping_option === 1 ? 'Delivery Time *' : 'Pickup Time *'}
                    </label>
                    <input
                      type="time"
                      name="pickup_time"
                      value={offerForm.pickup_time}
                      onChange={handleOfferFormChange}
                      required
                      step="60"
                      min={getMinimumTime(offerForm.pickup_date)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                      disabled={isSubmittingOffer}
                      onKeyDown={(e) => {
                        // Prevent manual typing but allow picker navigation
                        if (e.key !== 'Tab' && e.key !== 'Escape' && e.key !== 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => e.preventDefault()}
                      style={{
                        colorScheme: 'light',
                        cursor: 'pointer'
                      }}
                      onBlur={(e) => {
                        const minTime = getMinimumTime(offerForm.pickup_date);
                        if (minTime && e.target.value < minTime) {
                          toast.error('Time has been adjusted to the nearest future time', {
                            position: "top-center",
                            autoClose: 2000,
                          });
                          setOfferForm(prev => ({ ...prev, pickup_time: minTime }));
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeOfferModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                    disabled={isSubmittingOffer}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    disabled={isSubmittingOffer}
                  >
                    {isSubmittingOffer ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      'Submit Offer'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceDetailsPage;